// src/auth/AccountCreatedSuccess.js
import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight } from 'lucide-react';
import { useAuth } from './SupabaseAuthProvider';

const AccountCreatedSuccess = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get user data from navigation state if available
  const userData = location.state?.user || user;

  // If user is not authenticated, redirect to login
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // If no user data exists in the state or context, this page was likely accessed directly
  // rather than from the signup flow
  if (!userData && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-yellow-500 mb-4">
            <Shield className="h-12 w-12 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            No Account Data Found
          </h1>
          <p className="text-gray-600 mb-6">
            This page is meant to be accessed after creating a new account. It
            seems you've accessed it directly.
          </p>
          <Link
            to="/app/dashboard"
            className="inline-block w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const companyName = userData?.user_metadata?.company_name || '';
  const ownerName = userData?.user_metadata?.owner_name || '';
  const plan = userData?.user_metadata?.plan || 'starter';

  // Format plan name for display
  const formatPlanName = (planId) => {
    const plans = {
      starter: 'Starter',
      professional: 'Professional',
      business: 'Business',
    };
    return plans[planId] || planId.charAt(0).toUpperCase() + planId.slice(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-8 text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to TrackerSheets!
          </h1>

          <p className="text-gray-600 mb-6">
            Your account has been successfully created. You're all set to start
            tracking and managing your client statuses.
          </p>

          {/* Account Details */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Account Details
            </h2>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Company Name</p>
                <p className="font-medium text-gray-900">{companyName}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Account Owner</p>
                <p className="font-medium text-gray-900">{ownerName}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Subscription Plan</p>
                <div className="flex items-center">
                  <span className="font-medium text-gray-900 mr-2">
                    {formatPlanName(plan)}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    14-day Trial
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="space-y-4 mb-8">
            <h2 className="text-lg font-medium text-gray-900">Next Steps</h2>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-3">
                <span className="text-blue-600 text-sm font-bold">1</span>
              </div>
              <p className="text-sm text-gray-600 text-left">
                Complete your account setup by adding your business logo and
                customizing your portal appearance.
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-3">
                <span className="text-blue-600 text-sm font-bold">2</span>
              </div>
              <p className="text-sm text-gray-600 text-left">
                Create your first client portal and invite your clients to view
                their project status.
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-3">
                <span className="text-blue-600 text-sm font-bold">3</span>
              </div>
              <p className="text-sm text-gray-600 text-left">
                Explore the dashboard to get familiar with all the features
                available to you.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <Link
              to="/app/dashboard"
              className="w-full flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Dashboard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>

            <Link
              to="/app/setup"
              className="w-full flex justify-center items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Complete Setup
            </Link>
          </div>
        </div>

        {/* Footer with Support Link */}
        <div className="bg-gray-50 px-8 py-4 text-center">
          <p className="text-sm text-gray-600">
            Need help getting started?
            <a
              href="/support"
              className="text-blue-600 hover:text-blue-800 ml-1"
            >
              Contact our support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountCreatedSuccess;
